import {
  AutomationTemplateIds,
  automationTemplateIdSchema,
  automationTemplates,
  buildDefaultAutomationSettings,
  checkIsTeammateCoachingSettings,
  getAutomationPlaybookIds,
  getAutomationTemplateById,
  type Automation,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  useAllAutomationsQuery,
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
} from "../../api/entities/automation.js";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { AutomationsTable } from "../../features/automations/automationsTable.js";
import { AutomationTemplateChooserDialog } from "../../features/automations/automationTemplateChooserDialog.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { FilterResultSummary } from "../../features/filters/filterResultSummary.js";
import {
  Can,
  useTeammateAuthorization,
} from "../../helpers/teammateAuthorizationContext.js";

const statusFilters = [
  { id: "active", label: "Active" },
  { id: "all", label: "All" },
] as const;

type StatusFilter = (typeof statusFilters)[number]["id"];

const ALL_PLAYBOOKS = "all-playbooks";
const ALL_TEMPLATES = "all-templates";

export const AgentsPage: FC = () => {
  const navigate = useNavigate();
  const { playbookId: playbookIdParam } = useParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [playbookFilter, setPlaybookFilter] = useState<string>(ALL_PLAYBOOKS);
  const [templateFilter, setTemplateFilter] = useState<string>(ALL_TEMPLATES);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);

  const teammateAuthorization = useTeammateAuthorization();
  // Reviewers can view agents but not modify them, so the enable switches
  // render disabled.
  const isReadOnly = !teammateAuthorization.can(
    PermissionActions.UPDATE,
    PermissionSubjects.AUTOMATIONS,
  );

  const { data: automations, isLoading } = useAllAutomationsQuery();
  const { data: playbooks } = useAllMinimalPlaybooksQuery({
    shouldExcludeTeammateAgentPlaybooks: true,
  });
  const [updateAutomation] = useUpdateAutomationMutation();
  const [createAutomation, { isLoading: isCreating }] =
    useCreateAutomationMutation();

  const playbookNamesById = useMemo(
    () =>
      new Map<PlaybookId, string>(
        playbooks?.map((playbook) => [playbook.id, playbook.name]) ?? [],
      ),
    [playbooks],
  );

  // Playbooks that already have a coaching agent, so a new coaching agent
  // defaults to a free playbook (uniqueness is enforced server-side).
  const coachingPlaybookIdsInUse = useMemo(
    () =>
      new Set<PlaybookId>(
        (automations ?? []).flatMap((automation) =>
          automation.template_id === AutomationTemplateIds.TEAMMATE_COACHING &&
          checkIsTeammateCoachingSettings(automation.settings)
            ? [automation.settings.playbook_id]
            : [],
        ),
      ),
    [automations],
  );

  const playbookFilterOptions = useMemo<readonly SimpleSelectOption<string>[]>(
    () => [
      { value: ALL_PLAYBOOKS, label: "All playbooks" },
      ...(playbooks?.map((playbook) => ({
        value: playbook.id,
        label: playbook.name,
      })) ?? []),
    ],
    [playbooks],
  );

  const templateFilterOptions = useMemo<readonly SimpleSelectOption<string>[]>(
    () => [
      { value: ALL_TEMPLATES, label: "All agents" },
      ...automationTemplateIdSchema.options.map((templateId) => ({
        value: templateId,
        label: getAutomationTemplateById(templateId).name,
      })),
    ],
    [],
  );

  const filteredAutomations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (automations ?? []).filter((automation) => {
      if (statusFilter === "active" && !automation.is_enabled) {
        return false;
      }
      if (
        playbookFilter !== ALL_PLAYBOOKS &&
        !getAutomationPlaybookIds(automation.settings).some(
          (playbookId) => playbookId === playbookFilter,
        )
      ) {
        return false;
      }
      if (
        templateFilter !== ALL_TEMPLATES &&
        automation.template_id !== templateFilter
      ) {
        return false;
      }
      if (query === "") {
        return true;
      }
      const template = getAutomationTemplateById(automation.template_id);
      return (
        automation.name.toLowerCase().includes(query) ||
        template.name.toLowerCase().includes(query)
      );
    });
  }, [automations, statusFilter, playbookFilter, templateFilter, searchQuery]);

  const hasFilterChanges =
    searchQuery.trim() !== "" ||
    playbookFilter !== ALL_PLAYBOOKS ||
    templateFilter !== ALL_TEMPLATES ||
    statusFilter !== "all";

  const onResetFilters = () => {
    setSearchQuery("");
    setPlaybookFilter(ALL_PLAYBOOKS);
    setTemplateFilter(ALL_TEMPLATES);
    setStatusFilter("all");
  };

  // Agents auto-save, so creation is immediate: we persist a disabled agent
  // seeded with this route's playbook and default settings, then open it so the
  // user can configure and preview it right away.
  const onCreateAgent = async (templateId: AutomationTemplateId) => {
    if (!playbookIdParam) {
      throw new Error("Missing playbookId URL param");
    }
    // A coaching agent must be unique per playbook, so default it to a playbook
    // that doesn't have one yet; fall back to the route's playbook (the server
    // then rejects and we surface the conflict modal). Other templates keep
    // using the route's playbook.
    const playbookId =
      templateId === AutomationTemplateIds.TEAMMATE_COACHING
        ? (playbooks?.find(
            (playbook) => !coachingPlaybookIdsInUse.has(playbook.id),
          )?.id ?? asPlaybookId(playbookIdParam))
        : asPlaybookId(playbookIdParam);
    const template = getAutomationTemplateById(templateId);
    try {
      const automation = await createAutomation({
        template_id: templateId,
        name: template.name,
        is_enabled: false,
        settings: buildDefaultAutomationSettings(templateId, playbookId),
        session_id: uuidv4(),
      }).unwrap();
      navigate(automation.id);
    } catch (error) {
      if (isConflictQueryError(error)) {
        setHasCoachingConflict(true);
      }
      // Creation failed; stay on the chooser so the user can retry.
    }
  };

  const onToggleEnabled = async (
    automation: Automation,
    isEnabled: boolean,
  ) => {
    await updateAutomation({
      id: automation.id,
      is_enabled: isEnabled,
    });
  };

  return (
    <PageLayout height={PageLayoutHeight.Bounded}>
      <PageHeader
        title="Agents"
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.AUTOMATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <DialogTrigger>
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.SMALL}
                  icon={<IconPlus className="size-4 shrink-0" />}
                  isDisabled={!allowed}
                >
                  Create agent
                </Button>
                <AutomationTemplateChooserDialog
                  templates={automationTemplates}
                  isCreating={isCreating}
                  onChoose={(templateId: AutomationTemplateId) => {
                    // On success `onCreateAgent` navigates to the new agent
                    // (unmounting this page, which closes the dialog); on
                    // failure it leaves the dialog open to retry.
                    void onCreateAgent(templateId);
                  }}
                />
              </DialogTrigger>
            )}
          </Can>
        }
        filterBar={
          <FilterBar
            isLoading={isLoading}
            leadingItems={
              <div className="w-56 shrink-0">
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search agents…"
                  leftIcon={<IconSearch className="size-4 text-gray-400" />}
                  maxLength={100}
                  size={Size.SMALL}
                />
              </div>
            }
            items={
              <>
                <SimpleSelect
                  label="Playbook filter"
                  isLabelHidden
                  options={playbookFilterOptions}
                  value={playbookFilter}
                  onChange={setPlaybookFilter}
                  size={Size.SMALL}
                />
                <SimpleSelect
                  label="Agent template filter"
                  isLabelHidden
                  options={templateFilterOptions}
                  value={templateFilter}
                  onChange={setTemplateFilter}
                  size={Size.SMALL}
                />
              </>
            }
            resultSummary={
              <FilterResultSummary
                count={filteredAutomations.length}
                denominatorCount={
                  hasFilterChanges ? (automations?.length ?? 0) : undefined
                }
              />
            }
            onReset={hasFilterChanges ? onResetFilters : undefined}
            rightActions={
              <SegmentedControl
                aria-label="Status"
                size={Size.SMALL}
                options={statusFilters}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            }
          />
        }
      />

      <PageBody fills>
        <TableCard>
          <AutomationsTable
            automations={filteredAutomations}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            playbookNamesById={playbookNamesById}
            emptyMessage={
              (automations?.length ?? 0) > 0
                ? "No agents match your filters."
                : "No agents yet. Create your first agent to get started."
            }
            onAutomationAction={(automation) => {
              navigate(automation.id);
            }}
            onToggleEnabled={(automation, isEnabled) => {
              void onToggleEnabled(automation, isEnabled);
            }}
          />
        </TableCard>
      </PageBody>

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </PageLayout>
  );
};
